import { useEffect, useState } from "react";
import { api, formatCurrency, formatError } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function SettingsPage() {
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [services, setServices] = useState([]);
  const [sForm, setSForm] = useState({ name: "", value: 0, technician_value: 0 });
  const [editingS, setEditingS] = useState(null);
  const [sOpen, setSOpen] = useState(false);
  const [err, setErr] = useState("");

  const [complements, setComplements] = useState([]);
  const [cForm, setCForm] = useState({ name: "", value: 0, description: "" });
  const [editingC, setEditingC] = useState(null);
  const [cOpen, setCOpen] = useState(false);
  const [cErr, setCErr] = useState("");

  // Maintenance thresholds
  const [maint, setMaint] = useState({ vehicle_km_threshold: 10000, tool_out_months_threshold: 3 });
  const [maintSaved, setMaintSaved] = useState(false);

  const load = () => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/service-types").then((r) => setServices(r.data));
    api.get("/complements").then((r) => setComplements(r.data));
    api.get("/maintenance/settings").then((r) => setMaint(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    if (!newCat.trim()) return;
    await api.post("/categories", { name: newCat });
    setNewCat(""); load();
  };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); load(); };

  const openService = (s) => {
    if (s) {
      setEditingS(s.id);
      setSForm({ name: s.name, value: s.value ?? 0, technician_value: s.technician_value ?? 0 });
    } else {
      setEditingS(null);
      setSForm({ name: "", value: 0, technician_value: 0 });
    }
    setErr(""); setSOpen(true);
  };
  const saveService = async () => {
    setErr("");
    try {
      const payload = {
        name: sForm.name,
        value: Number(sForm.value) || 0,
        technician_value: Number(sForm.technician_value) || 0,
      };
      if (editingS) await api.put(`/service-types/${editingS}`, payload);
      else await api.post("/service-types", payload);
      setSOpen(false); load();
    } catch (e) { setErr(formatError(e)); }
  };
  const delService = async (id) => { if (window.confirm("Excluir tipo de serviço?")) { await api.delete(`/service-types/${id}`); load(); } };

  const openComp = (c) => {
    if (c) { setEditingC(c.id); setCForm({ name: c.name, value: c.value, description: c.description || "" }); }
    else { setEditingC(null); setCForm({ name: "", value: 0, description: "" }); }
    setCErr(""); setCOpen(true);
  };
  const saveComp = async () => {
    setCErr("");
    try {
      const payload = { name: cForm.name, value: Number(cForm.value), description: cForm.description };
      if (editingC) await api.put(`/complements/${editingC}`, payload);
      else await api.post("/complements", payload);
      setCOpen(false); load();
    } catch (e) { setCErr(formatError(e)); }
  };
  const delComp = async (id) => { if (window.confirm("Excluir complemento?")) { await api.delete(`/complements/${id}`); load(); } };

  const saveMaint = async () => {
    setMaintSaved(false);
    try {
      const r = await api.put("/maintenance/settings", {
        vehicle_km_threshold: Number(maint.vehicle_km_threshold) || 0,
        tool_out_months_threshold: Number(maint.tool_out_months_threshold) || 0,
      });
      setMaint(r.data);
      setMaintSaved(true);
      setTimeout(() => setMaintSaved(false), 2000);
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Ajustes" title="Configurações" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}><h3 className="label-caps">Categorias</h3></div>
          <div className="p-5">
            <div className="flex gap-2 mb-4">
              <input className="input flex-1" placeholder="Nova categoria" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-category-input" />
              <button className="btn btn-primary" onClick={addCat} data-testid="add-category-btn"><Plus size={14} /> Adicionar</button>
            </div>
            <div className="space-y-1">
              {cats.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 border" style={{ borderColor: "var(--border)" }}>
                  <span className="text-sm">{c.name}</span>
                  <button className="btn btn-ghost" onClick={() => delCat(c.id)}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <h3 className="label-caps">Valor de Serviços</h3>
            <button className="btn btn-primary" onClick={() => openService(null)} data-testid="new-service-btn"><Plus size={14} /> Novo</button>
          </div>
          <table className="data">
            <thead><tr><th>Serviço</th><th title="O que o técnico recebe por este serviço">Valor Técnico</th><th title="O que a empresa recebe do cliente">Valor Empresa</th><th></th></tr></thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id} data-testid={`service-row-${s.id}`}>
                  <td>{s.name}</td>
                  <td className="mono" data-testid={`service-tech-value-${s.id}`}>{formatCurrency(s.technician_value ?? 0)}</td>
                  <td className="mono" data-testid={`service-company-value-${s.id}`}>{formatCurrency(s.value ?? 0)}</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button className="btn btn-ghost" onClick={() => openService(s)} data-testid={`edit-service-${s.id}`}><Pencil size={14} /></button>
                      <button className="btn btn-ghost" onClick={() => delService(s.id)} data-testid={`del-service-${s.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {services.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm" style={{ color: "var(--text-mute)" }}>Sem tipos de serviço.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={sOpen} onClose={() => setSOpen(false)} title={editingS ? "Editar Serviço" : "Novo Serviço"} testid="service-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveService} data-testid="save-service-btn">Salvar</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid gap-3">
          <div><label className="label-caps block mb-1">Nome</label><input className="input" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} data-testid="service-name" /></div>
          <div>
            <label className="label-caps block mb-1">Valor Técnico (€)</label>
            <input className="input mono" type="number" step="0.01" value={sForm.technician_value} onChange={(e) => setSForm({ ...sForm, technician_value: e.target.value })} data-testid="service-technician-value" />
            <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>Quanto o técnico recebe por este serviço. É o que ele vê nas OTs.</div>
          </div>
          <div>
            <label className="label-caps block mb-1">Valor Empresa (€)</label>
            <input className="input mono" type="number" step="0.01" value={sForm.value} onChange={(e) => setSForm({ ...sForm, value: e.target.value })} data-testid="service-value" />
            <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>Quanto a empresa cobra do cliente. Visível apenas para admin/gestor.</div>
          </div>
        </div>
      </Modal>

      <div className="mt-6 card">
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="label-caps">Complementos (Adicionais de OT)</h3>
            <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>Adicionais como decodificador, repetidor, ponto extra que agregam valor à OT.</div>
          </div>
          <button className="btn btn-primary" onClick={() => openComp(null)} data-testid="new-complement-btn"><Plus size={14} /> Novo Complemento</button>
        </div>
        <table className="data">
          <thead><tr><th>Nome</th><th>Descrição</th><th>Valor</th><th></th></tr></thead>
          <tbody>
            {complements.map((c) => (
              <tr key={c.id} data-testid={`complement-row-${c.id}`}>
                <td>{c.name}</td>
                <td className="text-sm" style={{ color: "var(--text-dim)" }}>{c.description || "-"}</td>
                <td className="mono">{formatCurrency(c.value)}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button className="btn btn-ghost" onClick={() => openComp(c)}><Pencil size={14} /></button>
                    <button className="btn btn-ghost" onClick={() => delComp(c.id)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {complements.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm" style={{ color: "var(--text-mute)" }}>Sem complementos.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-6 card">
        <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <h3 className="label-caps">Alertas de Manutenção</h3>
          <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
            Aparecem no Dashboard quando um veículo passa do limite de km desde a última revisão ou uma ferramenta fica muito tempo com o técnico.
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-caps block mb-1">Limite de KM por veículo</label>
            <input
              className="input mono"
              type="number"
              min="0"
              value={maint.vehicle_km_threshold}
              onChange={(e) => setMaint({ ...maint, vehicle_km_threshold: e.target.value })}
              data-testid="maint-km-threshold"
            />
            <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>Ex.: 10000 km desde a última revisão registrada</div>
          </div>
          <div>
            <label className="label-caps block mb-1">Limite de meses fora (ferramentas)</label>
            <input
              className="input mono"
              type="number"
              min="0"
              value={maint.tool_out_months_threshold}
              onChange={(e) => setMaint({ ...maint, tool_out_months_threshold: e.target.value })}
              data-testid="maint-months-threshold"
            />
            <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>Ex.: 3 meses com o mesmo técnico</div>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <button className="btn btn-primary" onClick={saveMaint} data-testid="save-maint-btn">Salvar limites</button>
            {maintSaved && <span className="text-xs" style={{ color: "#4ADE80" }} data-testid="maint-saved-flag">✓ Limites atualizados</span>}
          </div>
        </div>
      </div>

      <Modal open={cOpen} onClose={() => setCOpen(false)} title={editingC ? "Editar Complemento" : "Novo Complemento"} testid="complement-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveComp} data-testid="save-complement-btn">Salvar</button>
        </>}>
        {cErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{cErr}</div>}
        <div className="grid gap-3">
          <div><label className="label-caps block mb-1">Nome</label><input className="input" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} placeholder="Ex.: Decodificador Adicional" data-testid="complement-name" /></div>
          <div><label className="label-caps block mb-1">Valor (€)</label><input className="input mono" type="number" step="0.01" value={cForm.value} onChange={(e) => setCForm({ ...cForm, value: e.target.value })} data-testid="complement-value" /></div>
          <div><label className="label-caps block mb-1">Descrição</label><input className="input" value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} placeholder="Opcional" /></div>
        </div>
      </Modal>
    </div>
  );
}
